import verbSeeds from '../data/verbs.json';
import { matchesVerbForm, normalizeVerb, splitVerbFormAlternatives } from '../lib/verbs';
import type { IrregularVerb, Level, VerbDistractorPair } from '../types';

const LEVEL_ORDER: Record<Level, number> = {
  A1: 0,
  A2: 1,
  B1: 2,
  B2: 3,
};

export const TRAINING_SESSION_SIZE = 10;

export const allVerbs: IrregularVerb[] = (verbSeeds as Array<Record<string, unknown>>)
  .map((verb) => normalizeVerb(verb))
  .sort((left, right) => left.infinitive.localeCompare(right.infinitive));

function shuffleItems<T>(items: T[]) {
  const next = [...items];

  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }

  return next;
}

function buildVerbFormKey(value: string) {
  return splitVerbFormAlternatives(value).sort().join('|');
}

function uniqueValues(values: string[]) {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    const key = buildVerbFormKey(trimmed);
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    result.push(trimmed);
  });

  return result;
}

export function getAllVerbs() {
  return allVerbs;
}

export function sampleTrainingVerbs(count = TRAINING_SESSION_SIZE) {
  return shuffleItems(allVerbs).slice(0, Math.min(count, allVerbs.length));
}

export function getVerbLevel(verb: IrregularVerb): Level {
  return verb.level;
}

export function getAcceptedVerbForms(
  verb: IrregularVerb,
  formType: 'pastSimple' | 'pastParticiple',
) {
  const canonical = formType === 'pastSimple' ? verb.pastSimple : verb.pastParticiple;
  const explicit = formType === 'pastSimple' ? verb.accepted.pastSimple : verb.accepted.pastParticiple;

  return uniqueValues([...splitVerbFormAlternatives(canonical), ...explicit]);
}

export function getVerbDistractorForms(
  verb: IrregularVerb,
  formType: 'pastSimple' | 'pastParticiple',
) {
  const values = formType === 'pastSimple' ? verb.distractors.pastSimple : verb.distractors.pastParticiple;
  return uniqueValues(values);
}

export function getVerbWrongPairs(verb: IrregularVerb): VerbDistractorPair[] {
  return verb.distractors.wrongPairs.filter((pair) => pair.pastSimple && pair.pastParticiple);
}

export function getLevelCounts() {
  return allVerbs.reduce<Record<Level, number>>(
    (counts, verb) => {
      counts[getVerbLevel(verb)] += 1;
      return counts;
    },
    { A1: 0, A2: 0, B1: 0, B2: 0 },
  );
}

export function getTagCounts() {
  const counts = new Map<string, number>();

  allVerbs.forEach((verb) => {
    verb.tags.forEach((tag) => {
      if (tag in LEVEL_ORDER) {
        return;
      }

      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    });
  });

  return Array.from(counts.entries())
    .map(([id, count]) => ({ id, count }))
    .sort((left, right) => right.count - left.count || left.id.localeCompare(right.id));
}

export function buildMultipleChoiceOptions(
  verb: IrregularVerb,
  formType: 'pastSimple' | 'pastParticiple',
  optionCount = 4,
) {
  const correctAnswer = formType === 'pastSimple' ? verb.pastSimple : verb.pastParticiple;
  const acceptedAnswers = getAcceptedVerbForms(verb, formType);

  const seededWrongOptions = getVerbDistractorForms(verb, formType).filter(
    (value) => !matchesVerbForm(value, acceptedAnswers),
  );

  const fallbackWrongOptions = shuffleItems(
    allVerbs
      .filter((item) => item.id !== verb.id)
      .map((item) => (formType === 'pastSimple' ? item.pastSimple : item.pastParticiple))
      .filter((value) => !matchesVerbForm(value, acceptedAnswers)),
  );

  return shuffleItems(uniqueValues([correctAnswer, ...seededWrongOptions, ...fallbackWrongOptions]).slice(0, optionCount));
}

export function formatVerbForm(value: string | string[]) {
  return splitVerbFormAlternatives(value).join(' / ');
}

export function sortVerbsByLevelAndName(verbs: IrregularVerb[]) {
  return [...verbs].sort((left, right) => {
    const byLevel = LEVEL_ORDER[left.level] - LEVEL_ORDER[right.level];
    if (byLevel !== 0) {
      return byLevel;
    }

    return left.infinitive.localeCompare(right.infinitive);
  });
}
